import React from 'react';
import {
  Box,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const DOC_LINKS = {
  'admin-guide': '/admin/documentation/admin-guide',
  'etl-operations': '/admin/documentation/etl-operations',
  'ADMIN_GUIDE.md': '/admin/documentation/admin-guide',
  'ETL_OPERATIONS_ADMIN_GUIDE.md': '/admin/documentation/etl-operations'
};

function resolveHref(href) {
  if (!href) return null;
  if (href.startsWith('/admin/documentation/')) return href;
  const slug = href.replace(/\.md$/i, '').replace(/^.*\//, '');
  if (DOC_LINKS[slug]) return DOC_LINKS[slug];
  if (DOC_LINKS[href]) return DOC_LINKS[href];
  if (href.startsWith('http://') || href.startsWith('https://')) return href;
  return null;
}

function renderInline(text, keyPrefix) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(<span key={`${keyPrefix}-t-${i++}`}>{text.slice(last, match.index)}</span>);
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(
        <Box component="strong" key={`${keyPrefix}-b-${i++}`} sx={{ fontWeight: 700 }}>
          {token.slice(2, -2)}
        </Box>
      );
    } else if (token.startsWith('`')) {
      parts.push(
        <Box
          component="code"
          key={`${keyPrefix}-c-${i++}`}
          sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}
        >
          {token.slice(1, -1)}
        </Box>
      );
    } else if (token.startsWith('[')) {
      const m = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        const route = resolveHref(m[2]);
        if (route) {
          parts.push(
            <Link component={RouterLink} to={route} key={`${keyPrefix}-l-${i++}`}>
              {m[1]}
            </Link>
          );
        } else {
          parts.push(
            <Typography component="span" key={`${keyPrefix}-l-${i++}`} color="text.secondary">
              {m[1]} ({m[2]})
            </Typography>
          );
        }
      }
    }
    last = match.index + token.length;
  }
  if (last < text.length) {
    parts.push(<span key={`${keyPrefix}-t-${i++}`}>{text.slice(last)}</span>);
  }
  return parts.length ? parts : text;
}

function parseTableRow(line) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isTableSeparator(line) {
  return /^\|?[\s:-]+\|[\s|:-]+\|?$/.test(line.trim());
}

function MarkdownDocViewer({ content }) {
  if (!content) return null;

  const blocks = [];
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i += 1;
      }
      blocks.push(
        <Paper
          key={`code-${blocks.length}`}
          variant="outlined"
          sx={{ p: 2, mb: 2, bgcolor: 'grey.50', overflow: 'auto' }}
        >
          {lang === 'mermaid' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Architecture diagram (Mermaid — render in GitHub or repo docs viewer)
            </Typography>
          )}
          <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
            {codeLines.join('\n')}
          </Box>
        </Paper>
      );
      i += 1;
      continue;
    }

    if (line.trim().startsWith('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = parseTableRow(line);
      i += 2;
      const bodyRows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        bodyRows.push(parseTableRow(lines[i]));
        i += 1;
      }
      blocks.push(
        <Box key={`table-${blocks.length}`} sx={{ overflowX: 'auto', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {header.map((cell, idx) => (
                  <TableCell key={idx}>{renderInline(cell, `th-${idx}`)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bodyRows.map((row, rIdx) => (
                <TableRow key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <TableCell key={cIdx}>{renderInline(cell, `td-${rIdx}-${cIdx}`)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
      continue;
    }

    if (line.startsWith('# ')) {
      blocks.push(
        <Typography key={`h1-${blocks.length}`} variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
          {renderInline(line.slice(2), `h1-${blocks.length}`)}
        </Typography>
      );
      i += 1;
      continue;
    }

    if (line.startsWith('## ')) {
      blocks.push(
        <Typography key={`h2-${blocks.length}`} variant="h5" component="h2" gutterBottom sx={{ mt: 2 }}>
          {renderInline(line.slice(3), `h2-${blocks.length}`)}
        </Typography>
      );
      i += 1;
      continue;
    }

    if (line.startsWith('### ')) {
      blocks.push(
        <Typography key={`h3-${blocks.length}`} variant="h6" component="h3" gutterBottom sx={{ mt: 1.5 }}>
          {renderInline(line.slice(4), `h3-${blocks.length}`)}
        </Typography>
      );
      i += 1;
      continue;
    }

    if (line.trim() === '---') {
      blocks.push(<Divider key={`hr-${blocks.length}`} sx={{ my: 2 }} />);
      i += 1;
      continue;
    }

    if (/^\d+\.\s/.test(line.trim())) {
      blocks.push(
        <Typography key={`ol-${blocks.length}`} variant="body1" component="p" gutterBottom sx={{ pl: 2 }}>
          {renderInline(line.trim(), `ol-${blocks.length}`)}
        </Typography>
      );
      i += 1;
      continue;
    }

    if (line.trim().startsWith('- ')) {
      blocks.push(
        <Typography key={`ul-${blocks.length}`} variant="body1" component="p" gutterBottom sx={{ pl: 2 }}>
          • {renderInline(line.trim().slice(2), `ul-${blocks.length}`)}
        </Typography>
      );
      i += 1;
      continue;
    }

    if (line.trim()) {
      blocks.push(
        <Typography key={`p-${blocks.length}`} variant="body1" component="p" gutterBottom>
          {renderInline(line, `p-${blocks.length}`)}
        </Typography>
      );
    } else {
      blocks.push(<Box key={`sp-${blocks.length}`} sx={{ height: '0.5em' }} />);
    }
    i += 1;
  }

  return <Box>{blocks}</Box>;
}

export default MarkdownDocViewer;
